import React from 'react';
import { Image, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { colors } from '../theme';

/**
 * The Homingo symbol on its own - six petals, no wordmark.
 *
 * Use this where the brand needs to be present but the name does not read at
 * size (the splash, an app icon, a watermark). Use `Logo` for the full lockup.
 *
 * ---------------------------------------------------------------------------
 * THIS IS THE REAL ARTWORK, NOT A REDRAW
 * ---------------------------------------------------------------------------
 * This used to be a hand-drawn SVG: six teardrops built from measured circle
 * geometry, on the reasoning that the symbol occupies only 63x70 pixels of
 * `logo_transparent.png` and upscaling that to a 96pt splash mark would be
 * soft.
 *
 * The cost of that reasoning was the whole brand. The redraw was an
 * APPROXIMATION of the supplied mark, and it appeared in six places - the home
 * header, the auth sheet, the account page, the splash, the version footer and
 * the profile avatar - while the actual artwork appeared in exactly one. Every
 * customer-facing surface was showing a shape that was close to the logo
 * rather than the logo, and the two do not match.
 *
 * So this crops the supplied file instead, exactly as `LogoWordmark` crops the
 * other half of it. Softness at a large size is a real trade and it is the
 * right one: a slightly soft correct mark beats a crisp wrong one, and the
 * sizes actually in use are 14–34pt, where there is nothing to see.
 */

/** Intrinsic size of `logo_transparent.png`. */
const SRC_W = 215;
const SRC_H = 135;

/**
 * The symbol's bounding box within that artwork.
 *
 * Measured off the file's alpha channel, not eyeballed: row 74 is the one
 * fully transparent row separating the mark from the wordmark, and the mark's
 * opaque extent above it is x76–138, y4–73. Re-measure rather than nudge these
 * if the artwork is ever resupplied.
 */
const BOX = { x: 76, y: 4, w: 63, h: 70 };

type Props = {
  /** Rendered size in points. The mark is square. */
  size?: number;
  /**
   * Recolours the symbol.
   *
   * The mark is a bitmap, so this is `tintColor` rather than a fill - it
   * replaces every opaque pixel with the colour and keeps the alpha, which is
   * what the callers tinting it white on green and grey in the footer need.
   * Pass `undefined` to keep the artwork's own dark green.
   */
  color?: string;
  style?: StyleProp<ViewStyle>;
};

function LogoMark({ size = 64, color = colors.primary, style }: Props) {
  /*
   * The mark is slightly taller than it is wide (63x70), so it is fitted to
   * the taller side and centred horizontally. Fitting to width instead would
   * let a 64pt request render 71pt tall and push every caller's layout down
   * by the difference.
   */
  const scale = size / BOX.h;

  return (
    <View
      style={[{ width: size, height: size }, styles.clip, style]}
      accessible
      accessibilityRole="image"
      accessibilityLabel="Homingo"
    >
      <Image
        source={require('../../assets/uploads/logo/logo_transparent.png')}
        style={{
          width: SRC_W * scale,
          height: SRC_H * scale,
          marginLeft: -BOX.x * scale + (size - BOX.w * scale) / 2,
          marginTop: -BOX.y * scale,
        }}
        resizeMode="stretch"
        tintColor={color}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  clip: {
    overflow: 'hidden',
  },
});

export default LogoMark;
